import { z } from "zod";

export const CUSTOM_APP_QUERY_PAGE_BYTES = 1024 * 1024;

const QueryScalarSchema = z.union([
  z
    .string()
    .max(1024)
    .refine((value) => !value.includes("\0"), "Remove null characters from the query value."),
  z.number().finite(),
  z.boolean(),
]);
const QueryFieldSchema = z
  .string()
  .min(1)
  .max(200)
  .refine((value) => !value.includes("\0"), "Remove null characters from the field name.");
const QueryAliasSchema = z.string().regex(/^[a-z][a-z0-9_]{0,62}$/);
const QueryPredicateSchema = z
  .object({
    eq: QueryScalarSchema.nullable().optional(),
    ne: QueryScalarSchema.optional(),
    gt: QueryScalarSchema.optional(),
    gte: QueryScalarSchema.optional(),
    lt: QueryScalarSchema.optional(),
    lte: QueryScalarSchema.optional(),
    in: z.array(QueryScalarSchema).min(1).max(50).optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, "Provide a filter operator such as eq or gte.");
const QueryFiltersSchema = z
  .record(QueryFieldSchema, QueryPredicateSchema)
  .refine((value) => Object.keys(value).length <= 16, "Use at most 16 filtered fields.");

export type CustomAppQueryExpression =
  | { field: string }
  | { value: string | number | boolean | null }
  | { aggregate: "sum" | "avg" | "min" | "max" | "count"; field?: string | undefined }
  | {
      op: "add" | "subtract" | "multiply" | "divide" | "coalesce";
      args: [CustomAppQueryExpression, CustomAppQueryExpression];
    };

function expressionSchema(depth: number): z.ZodType<CustomAppQueryExpression> {
  const leaf = z.union([
    z.object({ field: QueryFieldSchema }).strict(),
    z.object({ value: QueryScalarSchema.nullable() }).strict(),
    z
      .object({
        aggregate: z.enum(["sum", "avg", "min", "max", "count"]),
        field: QueryFieldSchema.optional(),
      })
      .strict()
      .refine(
        (value) => value.aggregate === "count" || value.field !== undefined,
        "Provide a field for this aggregate.",
      ),
  ]);
  if (depth === 0) return leaf;
  const child = expressionSchema(depth - 1);
  return z.union([
    leaf,
    z
      .object({
        op: z.enum(["add", "subtract", "multiply", "divide", "coalesce"]),
        args: z.tuple([child, child]),
      })
      .strict(),
  ]);
}

export const CustomAppQuerySchema = z
  .object({
    from: QueryFieldSchema,
    select: z
      .array(
        z.union([
          QueryFieldSchema,
          z.object({ as: QueryAliasSchema, expr: expressionSchema(3) }).strict(),
        ]),
      )
      .min(1)
      .max(32),
    where: QueryFiltersSchema.default({}),
    /** One reference join prevents independent one-to-many relationships multiplying aggregate inputs. */
    joins: z
      .array(
        z
          .object({
            from: QueryFieldSchema,
            as: QueryAliasSchema,
            type: z.enum(["left", "inner"]).default("left"),
            on: z.object({ field: QueryFieldSchema, equals: QueryFieldSchema }).strict(),
            where: QueryFiltersSchema.default({}),
          })
          .strict(),
      )
      .max(1)
      .default([]),
    groupBy: z.array(QueryFieldSchema).max(8).default([]),
    having: QueryFiltersSchema.default({}),
    orderBy: z
      .array(z.object({ field: QueryFieldSchema, direction: z.enum(["asc", "desc"]) }).strict())
      .max(1)
      .default([]),
    first: z.number().int().min(1).max(10000).default(50),
    after: z.string().max(CUSTOM_APP_QUERY_PAGE_BYTES).nullable().optional(),
  })
  .strict();

export type CustomAppQuery = z.infer<typeof CustomAppQuerySchema>;
export type CustomAppQueryInput = z.input<typeof CustomAppQuerySchema>;
export type CustomAppQueryPage = {
  items: { id: string | null; data: Record<string, unknown> }[];
  has_more: boolean;
  next_cursor: string | null;
};
